// src/lib/offline/replay.ts
import type {MutationQueue, MutationRecord} from './queue';
import {useQueueStore} from './queue-store';
import {getToken} from '@/lib/auth-token';

/** Maximum number of retry attempts per mutation before marking as failed */
const MAX_RETRIES = 3;

export interface ReplayResult {
  /** Number of mutations successfully replayed */
  replayed: number;
  /** Number of mutations that failed after all retries */
  failed: number;
  /** If replay was stopped early, the reason */
  stoppedReason?: 'auth-expired';
}

type FetchFn = (url: string, init: RequestInit) => Promise<Response>;

let replayLock = false;

/** @internal — only for testing */
export function resetReplayLock(): void {
  replayLock = false;
}

/**
 * Replay all pending mutations in the queue, in FIFO order.
 *
 * - Sequential: one at a time (order matters — can't update before create)
 * - 3 retries per mutation, then mark as failed
 * - On 401: stop replay immediately (session expired)
 * - Updates the queue store (pendingCount, failedCount, isReplaying) throughout
 *
 * @param queue - The mutation queue to replay
 * @param fetchFn - The fetch function to use (defaults to globalThis.fetch, injectable for testing)
 */
export async function replayQueue(
  queue: MutationQueue,
  fetchFn: FetchFn = globalThis.fetch.bind(globalThis),
): Promise<ReplayResult> {
  const result: ReplayResult = {replayed: 0, failed: 0};

  if (replayLock) {
    return result;
  }
  replayLock = true;

  const all = await queue.getAll();
  const pending = all.filter((m) => m.status === 'pending');

  if (pending.length === 0) {
    replayLock = false;
    return result;
  }

  const {setIsReplaying, setPendingCount, setFailedCount} = useQueueStore.getState();

  setIsReplaying(true);
  setPendingCount(pending.length);

  try {
    for (const mutation of pending) {
      const outcome = await replayOne(mutation, queue, fetchFn);

      if (outcome === 'success') {
        result.replayed++;
        setPendingCount(useQueueStore.getState().pendingCount - 1);
      } else if (outcome === 'failed') {
        result.failed++;
        setFailedCount(useQueueStore.getState().failedCount + 1);
        setPendingCount(useQueueStore.getState().pendingCount - 1);
      } else if (outcome === 'auth-expired') {
        result.stoppedReason = 'auth-expired';
        break;
      }
    }
  } finally {
    // Sync store with actual queue state
    const remaining = await queue.getAll();
    setPendingCount(remaining.filter((m) => m.status === 'pending').length);
    setFailedCount(remaining.filter((m) => m.status === 'failed').length);
    setIsReplaying(false);
    replayLock = false;
  }

  return result;
}

async function replayOne(
  mutation: MutationRecord,
  queue: MutationQueue,
  fetchFn: FetchFn,
): Promise<'success' | 'failed' | 'auth-expired'> {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // Re-attach current Bearer token from localStorage (not the stale
      // token from when the mutation was originally attempted). The
      // serializeMutation function strips Authorization headers (PDPA).
      const headers: Record<string, string> = {...mutation.headers};
      const token = getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetchFn(mutation.url, {
        method: mutation.method,
        headers,
        body: mutation.body,
      });

      if (response.status === 401) {
        return 'auth-expired';
      }

      if (response.ok) {
        await queue.dequeue(mutation.id);
        return 'success';
      }

      // Non-OK, non-401 — client errors (4xx) are not retryable
      if (response.status >= 400 && response.status < 500) {
        await queue.markFailed(mutation.id);
        return 'failed';
      }

      // Server errors (5xx) — retryable, mark failed only on last attempt
    } catch {
      // Network error — retryable
    }

    // Only mark as permanently failed on the last attempt
    if (attempt === MAX_RETRIES - 1) {
      await queue.markFailed(mutation.id);
    }
  }

  return 'failed';
}
